import { useMemo } from "react";

import { dateStringToDate } from "@/components/pages_logged_in/schedule/timeslots/date-string-to-date";
import { formatDate } from "@/utils/datetime";

import { CalendarWidget } from "./CalendarWidget";
import { SideBySideLoadingState } from "./SideBySideLoadingState";
import { TimezoneSelector } from "./TimezoneSelector";

interface SideBySideTimeslotLayoutProps {
  requiresBookingApproval: boolean;
  loading: boolean;
  isViewOnly: boolean;
  timeslots: string[];
  unavailableTimeslots: string[];
  dateString: string;
  onSelectTimeslots: (timeslots: string[]) => void;
  selectedTimeslots: string[];
  setDateString: (dateString: string) => void;
  timezone: string;
  setTimezone: (timezone: string) => void;
  availableDates: Record<string, boolean>;
}

export function SideBySideTimeslotLayout({
  requiresBookingApproval,
  loading,
  isViewOnly,
  timeslots,
  unavailableTimeslots,
  dateString,
  selectedTimeslots,
  setDateString,
  timezone,
  setTimezone,
  availableDates,
  onSelectTimeslots,
}: SideBySideTimeslotLayoutProps) {
  // Merge available and unavailable timeslots, sort chronologically
  const allTimeslots = useMemo(() => {
    return [...timeslots, ...unavailableTimeslots]
      .filter((timeslot, index, array) => array.indexOf(timeslot) === index) // Remove duplicates
      .sort((a, b) => new Date(a).getTime() - new Date(b).getTime()); // Sort chronologically
  }, [timeslots, unavailableTimeslots]);

  const toggleSelectTimeslot = (timeslot: string) => {
    if (requiresBookingApproval) {
      // Multiselect mode when booking approval is required
      if (selectedTimeslots.includes(timeslot)) {
        onSelectTimeslots(selectedTimeslots.filter((t) => t !== timeslot));
      } else {
        onSelectTimeslots([...selectedTimeslots, timeslot]);
      }
    } else {
      // Single select mode when no booking approval required
      if (selectedTimeslots.includes(timeslot)) {
        onSelectTimeslots([]);
      } else {
        onSelectTimeslots([timeslot]);
      }
    }
  };

  if (loading) {
    return <SideBySideLoadingState />;
  }

  return (
    <div className="flex">
      {/* Left side - Calendar */}
      <div className="w-110 mr-8">
        <CalendarWidget
          dateString={dateString}
          setDateString={setDateString}
          availableDates={availableDates}
        />
      </div>

      {/* Right side - Timeslots */}
      <div className="flex-1 ml-4">
        <div className="font-medium text-lg">
          {formatDate(
            dateStringToDate(dateString, timezone),
            "eeee, MMMM d, yyyy z",
            timezone,
          )}
        </div>

        <div className="mt-2 mr-8">
          <TimezoneSelector
            value={timezone}
            onChange={(tz) => tz && setTimezone(tz.value)}
          />
        </div>

        {/* Inlined TimeslotSelector functionality */}
        <div className="relative mt-4">
          {/* Timeslots container with fade effect */}
          <div className="relative overflow-hidden h-88">
            {/* Scrollable timeslots */}
            <div className="h-full overflow-y-auto pr-4">
              <div className="space-y-2 text-center font-medium mr-4 pb-12">
                {allTimeslots.map((timeslot) => {
                  const selected = selectedTimeslots.includes(timeslot);
                  const available =
                    unavailableTimeslots.length > 0 &&
                    !unavailableTimeslots.includes(timeslot);

                  const baseClasses =
                    "p-4 rounded-lg border-2 transition-colors flex justify-center items-center w-full";

                  const cursorClass = isViewOnly
                    ? "cursor-default"
                    : "cursor-pointer";

                  const conditionalClasses = selected
                    ? "border-blue-500 bg-blue-50 text-blue-600"
                    : "border-gray-300 bg-white text-gray-700 hover:border-gray-400";

                  const disabledClasses = isViewOnly
                    ? "border-gray-200 text-gray-500 bg-gray-50 cursor-default hover:border-gray-200 hover:text-gray-500 hover:bg-gray-50"
                    : "";

                  return (
                    <div
                      key={timeslot}
                      onClick={() =>
                        !isViewOnly && toggleSelectTimeslot(timeslot)
                      }
                      className={`${baseClasses} ${cursorClass} ${conditionalClasses} ${disabledClasses}`}
                    >
                      <div className="flex items-center justify-center">
                        <div className="flex h-2 w-2 mr-2">
                          {available && (
                            <svg height="8" width="8">
                              <circle r="4" cx="4" cy="4" fill="#27AE60" />
                            </svg>
                          )}
                        </div>
                        <span>{formatDate(timeslot, "h:mm a", timezone)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Bottom fade gradient */}
            <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white via-white/80 to-transparent z-10 pointer-events-none" />
          </div>
        </div>
      </div>
    </div>
  );
}
